import {
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  Grid2,
  IconButton,
  TextField,
  Typography
} from '@mui/material'
import ActionModal from 'asterX-UI/Modals/ActionModal'
import { CreditFlow } from 'lib/api/services/creditFlow.service'
import { enqueueSnackbar } from 'notistack'
import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { useContractStore } from 'context/ContractStore'

import { GRAY_COLORS, INFO_COLORS } from 'asterX-UI'
import { File04 } from 'asterX-UI/Icons/File04'
import { DeleteIcon } from 'asterX-UI/Icons/DeleteIcon'
import { formatFileSize, sleep } from 'lib/utils'
import { DatePicker } from '@mui/x-date-pickers'
import { collateralDataFields } from 'pages/NewApplication/new-application'
import FieldInfoNG from 'asterX-UI/DataDisplay/FieldInfoNG'
import { format, parse } from 'date-fns'
import { debounce } from 'lodash'
import { CheckCircleOutline, ErrorOutline } from '@mui/icons-material'
import { MODAL_CONFIG } from '../role-modals-constant'
import GaranteeFileModal from '../../GaranteeFileModal/GaranteeFileModal'
import { useCooldown } from '../../../lib/hooks'

const PledgeData = lazy(() => import('pages/NewApplication/components/PledgeData/PledgeData'))

const initialDate = {
  firstRepaymentDate: null,
  issuePlanDate: null,
  repaymentPlanDate: null
}

const initialDetailsData = {
  iik: null,
  name: null,
  bik: null,
  bin: null,
  kbe: null,
  settlementAccount: null,
  dealerBankBin: null
}

const dateFormat = 'dd.MM.yyyy'

const ManagerModal = ({ currentModal, closeModal = () => {}, source }) => {
  const fileInputRefs = useRef({})

  const {
    contractStore: {
      loanApplicationData,
      fileCategories,
      dealerCenter,
      alternatives,
      collateralFileTypes,
      clientFileTypes
    },
    contractStoreFunc: {
      getFileCategories,
      validateFields,
      setErrorFields,
      getBsData,
      getDealerCenterName,
      getAlternatives,
      updateRepaymentDate,
      getApplicationById,
      setFileCategories,
      resignDocuments
    }
  } = useContractStore()

  const [managerData, setManagerData] = useState([])
  const [filesByCategory, setFilesByCategory] = useState({})
  const [dates, setDates] = useState(initialDate)
  const [detailsData, setDetailsData] = useState(initialDetailsData)
  const [selectedAlternative, setSelectedAlternative] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const loanId = loanApplicationData?.id
  const cooldownKey = loanId ? `resign:${loanId}` : null
  const { isActive, secondsLeft, start } = useCooldown(cooldownKey, 120)

  const [statusByCategory, setStatusByCategory] = useState({})
  const [issuedGroups, setIssuedGroups] = useState({})
  const [selectedIssuedGroup, setSelectedIssuedGroup] = useState(null)

  const buildIssuedGroups = (categories = []) => {
    const map = {
      userUploadCollateralDocs: { key: 'collateral', title: 'Документы залога' },
      userUploadBankDocs: { key: 'bank', title: 'Документы банка' },
      userUploadGuaranteeDocs: { key: 'other', title: 'Прочие документы' }
    }
    const groups = {}

    categories.forEach(c => {
      const meta = map[c.step] || map.userUploadGuaranteeDocs
      if (!groups[meta.key]) groups[meta.key] = { key: meta.key, title: meta.title, categories: [] }
      groups[meta.key].categories.push(c)
    })
    const order = ['collateral', 'bank', 'other']
    const ordered = {}
    order.forEach(k => {
      if (groups[k]) ordered[k] = groups[k]
    })
    Object.keys(groups).forEach(k => {
      if (!ordered[k]) ordered[k] = groups[k]
    })

    return ordered
  }

  const countsByGroup = React.useMemo(() => {
    const out = {}
    Object.entries(issuedGroups).forEach(([k, v]) => {
      out[k] = v.categories?.length || 0
    })
    return out
  }, [issuedGroups])

  const issuedGroupCategories = React.useMemo(() => {
    if (!selectedIssuedGroup) return []
    return issuedGroups[selectedIssuedGroup]?.categories || []
  }, [issuedGroups, selectedIssuedGroup])

  const selectedFilesCountIssued = React.useMemo(() => {
    if (!selectedIssuedGroup) return 0
    const ids = issuedGroupCategories.map(c => String(c.id))
    return Object.entries(filesByCategory).filter(([cid]) => ids.includes(String(cid))).length
  }, [filesByCategory, issuedGroupCategories, selectedIssuedGroup])

  useEffect(() => {
    if (loanApplicationData?.contract) {
      setDates({
        firstRepaymentDate: loanApplicationData?.contract?.firstRepaymentDate,
        issuePlanDate: loanApplicationData?.contract?.issuePlanDate,
        repaymentPlanDate: loanApplicationData?.contract?.repaymentPlanDate
      })
    }
  }, [loanApplicationData])

  useEffect(() => {
    const bankAccount = dealerCenter?.[0]?.bankAccounts?.[0]
    setDetailsData({
      iik: bankAccount?.iik || '',
      name: bankAccount?.name || '',
      bik: bankAccount?.bik || '',
      kbe: bankAccount?.kbe || '',
      settlementAccount: bankAccount?.settlementAccount || '',
      bin: bankAccount?.bin || ''
    })
  }, [dealerCenter])

  useEffect(() => {
    if (currentModal === 'UPLOAD_DOCUMENTS' || currentModal === 'UPLOAD_ISSUED_DOCS') {
      getFileCategories(loanApplicationData?.id)
    }
    if (currentModal === 'ADD_COLLETERAL') {
      getFileCategories(loanApplicationData?.id)
      setFileCategories(filteredCategories)
      getBsData()
    }
    if (currentModal === 'CHOOSE_ALTERNATIVE') {
      getAlternatives(loanApplicationData?.id)
    }
    if (currentModal === 'ADD_DETAILS') {
      getDealerCenterName(getFirstMatchingDealership())
    }
  }, [currentModal])

  useEffect(() => {
    if (currentModal === 'UPLOAD_ISSUED_DOCS') {
      setSelectedIssuedGroup(null)
      setStatusByCategory({})
      setIssuedGroups(buildIssuedGroups(fileCategories || []))
    }
  }, [currentModal, fileCategories])

  useEffect(() => {
    return () => {
      fileInputRefs.current = {}
    }
  }, [])

  const getFirstMatchingDealership = () => {
    const matchingCollateral = loanApplicationData?.collaterals?.find(collateral => collateral?.dealership !== null)

    if (matchingCollateral) {
      const { dealership } = matchingCollateral
      return dealership
    }

    return null
  }

  const resetState = () => {
    closeModal()
    setFilesByCategory({})
    setDetailsData(initialDetailsData)
    setManagerData([])
    setErrorFields({})
    setSelectedAlternative(null)
    setIsUploading(false)
  }

  const handleClose = () => {
    if (currentModal === 'CHOOSE_ALTERNATIVE') {
      handleChooseAlternativeClose()
    } else {
      resetState()
    }
  }

  const handleDateChange = field => newValue => {
    const formattedDate = newValue ? format(newValue, dateFormat) : null
    setDates(prevDates => ({
      ...prevDates,
      [field]: formattedDate
    }))
  }

  const handleChangeDetails = (key, value) => {
    if (key === 'settlementAccount' && !!dealerCenter?.length) {
      const selectedAccount = dealerCenter?.[0]?.bankAccounts?.find(account => account?.settlementAccount === value)
      if (selectedAccount) {
        setDetailsData(prevState => ({
          ...prevState,
          iik: selectedAccount.iik,
          settlementAccount: selectedAccount.settlementAccount,
          name: selectedAccount.name,
          bin: selectedAccount.bin,
          bik: selectedAccount.bik,
          kbe: selectedAccount.kbe
        }))
      } else {
        setDetailsData(prevState => ({
          ...prevState,
          settlementAccount: value
        }))
      }
    } else {
      setDetailsData(prevState => ({
        ...prevState,
        [key]: value
      }))
    }
  }

  const fileInputClick = categoryId => {
    fileInputRefs.current[categoryId]?.click()
  }

  const handleFileUpload = (event, categoryId) => {
    const uploadedFile = event.target.files?.[0] || event.dataTransfer?.files?.[0]
    if (!uploadedFile) return

    if (uploadedFile) {
      setFilesByCategory(prev => ({ ...prev, [categoryId]: uploadedFile }))
    }
  }

  const handleDeleteFile = categoryId => {
    setFilesByCategory(prev => {
      const updated = { ...prev }
      delete updated[categoryId]
      return updated
    })

    if (fileInputRefs.current[categoryId]) {
      fileInputRefs.current[categoryId].value = ''
    }
  }

  const handleDragOver = event => {
    event.preventDefault()
    event.stopPropagation()
  }

  const handleDrop = (event, categoryId) => {
    event.preventDefault()
    event.stopPropagation()
    handleFileUpload(event, categoryId)
  }

  const handleConfirmManager = debounce(async () => {
    if (!loanApplicationData?.id) {
      enqueueSnackbar('Нет данных для обработки!', { variant: 'warning' })
      return
    }

    try {
      setIsUploading(true)
      switch (currentModal) {
        case 'CLIENT_REFUSE':
        case 'CLIENT_CONSENT':
        case 'CLIENT_CONSIDERING':
        case 'CLIENT_DECIDED':
        case 'SUBMIT_FOR_REVIEW':
          await handleSendLoanApplicationResult()
          break

        case 'SUBMIT_FOR_SIGN_BY_DEALER':
          await handleSendDocumentsToSign()
          break

        case 'UPLOAD_DOCUMENTS':
          await handleUploadDocuments()
          break

        case 'ADD_COLLETERAL':
          await handleAddCollaterals()
          break

        case 'ADD_DETAILS':
          await handleSendDDS()
          break

        case 'CHOOSE_ALTERNATIVE':
          await handleSendAlternative()
          break

        case 'CHANGE_ISSUE_DATE_MANAGER':
          await updateRepaymentDate({ ...dates, id: loanApplicationData?.contract?.id }, resetState())
          break

        case 'RESIGN_DOCUMENTS': {
          if (isActive) {
            enqueueSnackbar(`Переподписание уже запущено. Осталось ${secondsLeft} сек.`, { variant: 'info' })
            break
          }
          const ok = await resignDocuments(loanApplicationData.id)
          if (ok) {
            start()
            resetState()
          }
          break
        }

        case 'UPLOAD_ISSUED_DOCS':
          await handleUploadIssuedDocuments()
          break

        default:
          resetState()
      }
    } catch (error) {
      console.error('Ошибка в обработке действия:', error)
      enqueueSnackbar('Произошла ошибка при обработке запроса.', { variant: 'error' })
    } finally {
      setIsUploading(false)
    }
  }, 300)

  const handleChooseAlternativeClose = async () => {
    try {
      const data = {
        loanApplicationId: loanApplicationData?.id,
        clientRefuse: true,
        comment: ''
      }

      const res = await CreditFlow.loanApplicationResult(data)

      if (res?.data) {
        enqueueSnackbar('Успешно', { variant: 'info' })
        resetState()
      }
    } catch (error) {
      enqueueSnackbar('Ошибка', { variant: 'error' })
    }
  }

  const handleSendAlternative = async () => {
    try {
      const data = {
        loanApplicationId: loanApplicationData?.id,
        alternativeId: selectedAlternative
      }

      const res = await CreditFlow.sendAlternative(data)
      if (res?.data) {
        handleSendLoanApplicationResult()
        resetState()
      }
    } catch (error) {
      enqueueSnackbar('Ошибка при отправке альтернативы', { variant: 'error' })
    }
  }

  const handleSendDDS = async () => {
    if (!loanApplicationData?.id) {
      enqueueSnackbar('Нет данных для обработки!', { variant: 'warning' })
      return
    }

    const prepareDDSData = () => {
      const bankAccount = dealerCenter?.[0]?.bankAccounts?.find(
        account => account?.settlementAccount === detailsData?.settlementAccount
      )

      const createData = {
        dealerDataName: dealerCenter?.[0]?.bankAccounts?.[0].partnerLegalName || undefined,
        dealerDataBin: dealerCenter?.[0]?.bin || undefined
      }

      const updateData = {
        id: loanApplicationData?.dds?.id,
        dealerBankName: bankAccount?.name || detailsData?.name || undefined,
        dealerBankBik: bankAccount?.bik || detailsData?.bik || undefined,
        dealerBankKbe: bankAccount?.kbe || detailsData?.kbe || undefined,
        dealerBankIik: bankAccount?.iik || detailsData?.iik || undefined,
        dealerBankSettlementAccount: bankAccount?.settlementAccount || detailsData?.settlementAccount || undefined,
        dealerBankBin: bankAccount?.dealerBankBin || detailsData?.dealerBankBin || undefined
      }

      if (loanApplicationData?.dds?.id) {
        return {
          loanApplicationId: loanApplicationData.id,
          ...updateData
        }
      }

      return {
        loanApplicationIds: [loanApplicationData.id],
        ddsDTO: {
          ...createData,
          ...updateData
        }
      }
    }

    const ddsData = prepareDDSData()

    try {
      setIsUploading(true)
      let res
      if (loanApplicationData?.dds?.id) {
        res = await CreditFlow.updateDDS(ddsData)
      } else {
        res = await CreditFlow.createDDS(ddsData)
      }

      if (res?.data) {
        enqueueSnackbar('Данные успешно отправлены!', { variant: 'success' })
        resetState()
      }
    } catch (error) {
      enqueueSnackbar('Ошибка отправки данных', { variant: 'error' })
    } finally {
      setIsUploading(false)
    }
  }

  const handleAddCollaterals = async () => {
    if (!loanApplicationData?.id || !managerData?.length) {
      return enqueueSnackbar(loanApplicationData?.id ? 'Нет залогов для отправки' : 'Ошибка: отсутствует ID заявки', {
        variant: 'warning'
      })
    }

    const isCollateralValid = collateral => {
      const guaranteeMap = {
        CLIENT: collateral?.clientGuarantee,
        LEGAL_CLIENT: collateral?.legalGuarantee
      }
      const dataToValidate = guaranteeMap[collateral.type] || []
      const combinedData = { ...collateral, ...dataToValidate }
      return validateFields(collateralDataFields(collateral.type), combinedData)
    }

    const invalidCollaterals = managerData.filter(collateral => !isCollateralValid(collateral))
    if (invalidCollaterals.length) {
      enqueueSnackbar('Пожалуйста, заполните все обязательные поля, отмеченные красным.', {
        variant: 'info'
      })
      return
    }

    try {
      setIsUploading(true)
      const collateralsToSend = managerData.map(collateral => ({
        ...collateral,
        loanApplicationId: loanApplicationData.id,
        dealership: collateral?.dealership?.name,
        group: collateral?.group?.name,
        country: {
          name: collateral?.country?.name,
          bsId: collateral?.country?.countryId
        },
        documentType: {
          name: collateral?.documentType?.name,
          bsId: collateral?.documentType?.complementaryDocTypeId
        },
        education: {
          name: collateral?.education?.name,
          bsId: collateral?.education?.educationId
        },
        maritalStatus: {
          name: collateral?.maritalStatus?.name,
          bsId: collateral?.maritalStatus?.maritalStatusId
        },
        sexName: {
          name: collateral?.sexName?.name,
          bsId: collateral?.sexName?.sexId
        }
      }))

      if (!collateralsToSend.length) {
        return enqueueSnackbar('Нет залогов для отправки', { variant: 'warning' })
      }

      const res = await CreditFlow.createCollateral({ collaterals: collateralsToSend })

      if (!res?.data) {
        enqueueSnackbar(res?.msg?.message || 'Ошибка при отправке залогов', { variant: 'error' })
      } else {
        enqueueSnackbar('Успешно', { variant: 'success' })
        resetState()
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsUploading(false)
    }
  }

  const handleSendLoanApplicationResult = async () => {
    const data = {
      loanApplicationId: loanApplicationData?.id,
      comment: ''
    }
    if (currentModal === 'CLIENT_REFUSE') {
      data.clientRefuse = true
    }
    if (currentModal === 'CLIENT_CONSENT') {
      data.clientAgreement = true
    }
    if (currentModal === 'CLIENT_CONSIDERING') {
      data.clientConsidering = true
    }
    if (currentModal === 'CLIENT_DECIDED') {
      data.clientConsidering = false
    }
    if (currentModal === 'SUBMIT_FOR_REVIEW') {
      //Тут переделать ->
      data.clientConsidering = false
    }

    const res = await CreditFlow.loanApplicationResult(data)
    if (res?.data) {
      enqueueSnackbar('Успешно', { variant: 'info' })
      await sleep(500)
      await getApplicationById(loanApplicationData.id)
      resetState()
    }
  }

  const handleUploadDocuments = async () => {
    const entries = Object.entries(filesByCategory)

    if (entries.length === 0) {
      enqueueSnackbar('Не выбраны файлы для загрузки!', { variant: 'warning' })
      return
    }

    const uploadPromises = entries.map(async ([categoryId, file]) => {
      try {
        setIsUploading(true)
        const formData = new FormData()
        formData.append('file', file)
        formData.append('clientId', loanApplicationData?.clientId || '')
        formData.append('fileCategoryId', categoryId)
        formData.append('loanApplicationId', loanApplicationData?.id || '')

        const uploadRes = await CreditFlow.uploadFile(formData)
        if (!uploadRes?.data) throw new Error(`Ошибка загрузки файла для категории ${categoryId}`)

        return { categoryId, success: true }
      } catch (error) {
        return { categoryId, success: false, error }
      }
    })

    const results = await Promise.allSettled(uploadPromises)

    const successCount = results.filter(r => r.status === 'fulfilled' && r.value.success).length
    const failCount = results.filter(r => r.status === 'fulfilled' && !r.value.success).length

    if (successCount > 0) {
      enqueueSnackbar(`Успешно загружено: ${successCount}`, { variant: 'success' })
    }

    if (failCount > 0) {
      enqueueSnackbar(`Не удалось загрузить: ${failCount}`, { variant: 'error' })
    }

    resetState()
    await getApplicationById(loanApplicationData?.id)

    setIsUploading(false)
  }

  const handleSendDocumentsToSign = async () => {
    const res = await CreditFlow.sendDocumentsToSign(loanApplicationData?.id)
    if (res?.data) {
      enqueueSnackbar('Успешно', { variant: 'info' })
      await sleep(500)
      await getApplicationById(loanApplicationData.id)
      resetState()
    }
  }

  const handleUploadIssuedDocuments = async () => {
    if (!selectedIssuedGroup) {
      enqueueSnackbar('Сначала выберите группу документов', { variant: 'info' })
      return
    }

    const idsInGroup = issuedGroupCategories.map(c => String(c.id))
    const entries = Object.entries(filesByCategory).filter(([cid]) => idsInGroup.includes(String(cid)))

    if (entries.length === 0) {
      enqueueSnackbar('Не выбраны файлы для загрузки в выбранной группе', { variant: 'warning' })
      return
    }

    setIsUploading(true)
    setStatusByCategory(prev => {
      const next = { ...prev }
      entries.forEach(([cid]) => {
        next[cid] = 'pending'
      })
      return next
    })

    const tasks = entries.map(async ([categoryId, file]) => {
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('clientId', loanApplicationData?.clientId || '')
        formData.append('fileCategoryId', categoryId)
        formData.append('loanApplicationId', loanApplicationData?.id || '')
        const res = await CreditFlow.uploadFile(formData)
        if (!res?.data) throw new Error('upload failed')
        setStatusByCategory(prev => ({ ...prev, [categoryId]: 'success' }))
        return true
      } catch {
        setStatusByCategory(prev => ({ ...prev, [categoryId]: 'error' }))
        return false
      }
    })

    const results = await Promise.all(tasks)
    const ok = results.filter(Boolean).length
    const fail = results.length - ok
    if (ok) enqueueSnackbar(`Успешно загружено: ${ok}`, { variant: 'success' })
    if (fail) enqueueSnackbar(`Не удалось загрузить: ${fail}`, { variant: 'error' })

    resetState()
    await getApplicationById(loanApplicationData?.id)
    setIsUploading(false)
  }

  const disableConfirm = useMemo(() => {
    switch (currentModal) {
      case 'UPLOAD_DOCUMENTS':
        return isUploading || Object.keys(filesByCategory).length === 0
      case 'CHANGE_ISSUE_DATE_MANAGER':
        return !dates.firstRepaymentDate || !dates.issuePlanDate || !dates.repaymentPlanDate
      case 'ADD_DETAILS':
        return !detailsData.settlementAccount
      case 'CHOOSE_ALTERNATIVE':
        return !selectedAlternative
      case 'RESIGN_DOCUMENTS':
        return isUploading || isActive
      case 'UPLOAD_ISSUED_DOCS':
        return isUploading || !selectedIssuedGroup || selectedFilesCountIssued === 0
      default:
        return false
    }
  }, [currentModal, dates, filesByCategory, detailsData, selectedAlternative, isUploading])

  const modalConfig = MODAL_CONFIG[currentModal || 'DEFAULT']

  const filteredCategories = useMemo(() => {
    if (loanApplicationData.status.code === 'userRevert') {
      return fileCategories?.filter(file => file.step === 'userRevert')
    }
    if (currentModal === 'UPLOAD_DOCUMENTS' && source === 'clientDocuments') {
      return fileCategories?.filter(
        file =>
          file?.step === 'userCreateLoanApplication' &&
          ![
            'PAYMENT_INVOICE',
            'PAYMENT_INVOICE_COLLATERAL_DOCS',
            'PURCHASE_SALE_AGREEMENT',
            'CLIENT_PHOTO_GBDFL',
            'INITIAL_PAYMENT_LETTER'
          ].includes(file.code)
      )
    }
    if (
      currentModal === 'ADD_COLLETERAL' &&
      ['userUploadCollateralDocs', 'userAlternative'].includes(loanApplicationData.status.code)
    ) {
      return fileCategories?.filter(file => clientFileTypes.includes(file.code))
    }
    if (loanApplicationData.status.code === 'userUploadBankDocs' && currentModal === 'UPLOAD_DOCUMENTS') {
      return fileCategories?.filter(file => file.step === 'userUploadBankDocs')
    }

    if (loanApplicationData.status.code === 'userUploadCollateralDocs' && currentModal === 'UPLOAD_DOCUMENTS') {
      return fileCategories?.filter(
        file =>
          (collateralFileTypes.includes(file.code) && file.step === 'userUploadCollateralDocs') ||
          file?.code === 'PAYMENT_INVOICE_COLLATERAL_DOCS'
      )
    }
    return fileCategories
  }, [fileCategories, source, loanApplicationData.status.code, currentModal])

  useEffect(() => {
    if (currentModal === 'RESIGN_DOCUMENTS' && isActive) {
      enqueueSnackbar(`Переподписание уже запущено. Осталось ${secondsLeft} сек.`, { variant: 'info' })
      closeModal()
    }
  }, [currentModal, isActive, secondsLeft, closeModal])

  const confirmLabel =
    currentModal === 'RESIGN_DOCUMENTS' && isActive ? `Подождите ${secondsLeft} сек.` : modalConfig?.labelConfirm

  return (
    <>
      <ActionModal
        open={!!currentModal}
        onClose={resetState}
        onCancel={handleClose}
        onConfirm={handleConfirmManager}
        title={modalConfig?.headTitle}
        labelConfirm={confirmLabel}
        labelCancel={modalConfig?.labelCancel}
        cancelBtnRed={modalConfig?.cancelBtnRed}
        color={modalConfig?.color}
        maxWidth={modalConfig?.maxWidth}
        disableConfirm={disableConfirm}
      >
        <Box display="flex" flexDirection="column">
          <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>
            {currentModal === 'UPLOAD_ISSUED_DOCS'
              ? selectedIssuedGroup
                ? issuedGroups[selectedIssuedGroup]?.title || 'Категории'
                : 'Выберите группу документов'
              : modalConfig?.title}
          </Typography>
          {modalConfig?.subtitle && <Typography variant="body2">{modalConfig?.subtitle}</Typography>}
          {isUploading ? (
            <Box display="flex" justifyContent="center" alignItems="center">
              <CircularProgress />
            </Box>
          ) : (
            <>
              {currentModal === 'ADD_COLLETERAL' && (
                <Suspense
                  fallback={
                    <Box display="flex" justifyContent="center" alignItems="center">
                      <CircularProgress />
                    </Box>
                  }
                >
                  <Box display="flex" flexDirection="column" gap="15px">
                    <PledgeData data={managerData} setData={setManagerData} />
                  </Box>
                </Suspense>
              )}
              {currentModal === 'CHANGE_ISSUE_DATE_MANAGER' && (
                <Box display="flex" flexDirection="column" gap="15px">
                  <Box display="flex" flexDirection="column">
                    <Typography variant="small">Дата плановой выдачи</Typography>
                    <DatePicker
                      value={dates?.issuePlanDate ? parse(dates.issuePlanDate, dateFormat, new Date()) : null}
                      onChange={handleDateChange('issuePlanDate')}
                      fullWidth
                      format={dateFormat}
                      KeyboardButtonProps={{
                        'aria-label': 'change date'
                      }}
                    />
                  </Box>
                  <Box display="flex" flexDirection="column" mt={1}>
                    <Typography variant="small">Дата первого погашения</Typography>
                    <DatePicker
                      value={dates?.firstRepaymentDate ? parse(dates.firstRepaymentDate, dateFormat, new Date()) : null}
                      onChange={handleDateChange('firstRepaymentDate')}
                      fullWidth
                      format={dateFormat}
                      KeyboardButtonProps={{
                        'aria-label': 'change date'
                      }}
                    />
                  </Box>
                  <Box display="flex" flexDirection="column">
                    <Typography variant="small">Дата планового погашения</Typography>
                    <DatePicker
                      value={dates?.repaymentPlanDate ? parse(dates.repaymentPlanDate, dateFormat, new Date()) : null}
                      onChange={handleDateChange('repaymentPlanDate')}
                      fullWidth
                      format={dateFormat}
                      KeyboardButtonProps={{
                        'aria-label': 'change date'
                      }}
                    />
                  </Box>
                </Box>
              )}
              {currentModal === 'ADD_DETAILS' && (
                <Grid2 container spacing={4}>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="small">ФИО</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {loanApplicationData?.clientFullName}
                    </Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="small">ИИН клиента</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {loanApplicationData?.client?.iin}
                    </Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="small">Номер счета</Typography>
                    <Autocomplete
                      freeSolo
                      options={dealerCenter?.[0]?.bankAccounts?.map(account => account?.settlementAccount) || []}
                      value={detailsData.settlementAccount}
                      onInputChange={(_, value) => handleChangeDetails('settlementAccount', value)}
                      onChange={(_, value) => handleChangeDetails('settlementAccount', value)}
                      renderInput={params => (
                        <TextField
                          {...params}
                          fullWidth
                          variant="outlined"
                          size="small"
                          placeholder="Введите или выберите номер счета"
                        />
                      )}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="small">Название банка</Typography>
                    <Autocomplete
                      freeSolo
                      options={dealerCenter?.[0]?.bankAccounts?.map(account => account?.name) || []}
                      value={detailsData.name}
                      onInputChange={(_, value) => handleChangeDetails('name', value)}
                      onChange={(_, value) => handleChangeDetails('name', value)}
                      renderInput={params => (
                        <TextField
                          {...params}
                          fullWidth
                          variant="outlined"
                          size="small"
                          placeholder="Введите или выберите название банка"
                        />
                      )}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="small">БИК банка</Typography>
                    <Autocomplete
                      freeSolo
                      options={dealerCenter?.[0]?.bankAccounts?.map(account => account?.bik) || []}
                      value={detailsData.bik}
                      onInputChange={(_, value) => handleChangeDetails('bik', value)}
                      onChange={(_, value) => handleChangeDetails('bik', value)}
                      renderInput={params => (
                        <TextField
                          {...params}
                          fullWidth
                          variant="outlined"
                          size="small"
                          placeholder="Введите или выберите БИК банка"
                        />
                      )}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="small">КБЕ</Typography>
                    <Autocomplete
                      freeSolo
                      options={dealerCenter?.[0]?.bankAccounts?.map(account => account?.kbe) || []}
                      value={detailsData.kbe}
                      onInputChange={(_, value) => handleChangeDetails('kbe', value)}
                      onChange={(_, value) => handleChangeDetails('kbe', value)}
                      renderInput={params => (
                        <TextField
                          {...params}
                          fullWidth
                          variant="outlined"
                          size="small"
                          placeholder="Введите или выберите КБЕ"
                        />
                      )}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="small">ИИК</Typography>
                    <Autocomplete
                      freeSolo
                      options={dealerCenter?.[0]?.bankAccounts?.map(account => account?.iik) || []}
                      value={detailsData.iik}
                      onInputChange={(_, value) => handleChangeDetails('iik', value)}
                      onChange={(_, value) => handleChangeDetails('iik', value)}
                      renderInput={params => (
                        <TextField
                          {...params}
                          fullWidth
                          variant="outlined"
                          size="small"
                          placeholder="Введите или выберите ИИК"
                        />
                      )}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="small">Бин Банка Получателя</Typography>
                    <Autocomplete
                      freeSolo
                      options={dealerCenter?.[0]?.bankAccounts?.map(account => account?.dealerBankBin) || []}
                      value={detailsData.bin}
                      onInputChange={(_, value) => handleChangeDetails('dealerBankBin', value)}
                      onChange={(_, value) => handleChangeDetails('dealerBankBin', value)}
                      getOptionLabel={option => option ?? ''}
                      isOptionEqualToValue={(option, value) => option === value}
                      renderInput={params => (
                        <TextField
                          {...params}
                          fullWidth
                          variant="outlined"
                          size="small"
                          placeholder="Введите или выберите Бин Банка Получателя"
                        />
                      )}
                    />
                  </Grid2>
                </Grid2>
              )}
              {currentModal === 'UPLOAD_DOCUMENTS' && (
                <Box display="flex" flexDirection="column" gap="15px">
                  {filteredCategories.map(category => {
                    const file = filesByCategory[category.id]

                    return !file ? (
                      <Box
                        key={category?.id}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        gap={2}
                        sx={{
                          border: `1px solid ${GRAY_COLORS[300]}`,
                          borderRadius: '12px',
                          padding: '16px',
                          cursor: 'pointer'
                        }}
                        onClick={() => fileInputClick(category.id)}
                        onDragOver={handleDragOver}
                        onDrop={e => handleDrop(e, category.id)}
                      >
                        <input
                          id={`fileInput-${category.id}`}
                          type="file"
                          ref={el => (fileInputRefs.current[category.id] = el)}
                          hidden
                          onChange={e => handleFileUpload(e, category.id)}
                        />

                        <Box display="flex" flexDirection="column" alignItems="center">
                          <Typography sx={{ fontSize: 14, fontWeight: 600, textAlign: 'center' }} color="info">
                            {category?.name}
                          </Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 400, textAlign: 'center' }}>
                            Нажмите, чтобы загрузить
                          </Typography>
                          <Typography variant="small">Или перетащите документ в эту область</Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        key={category?.id}
                        display="flex"
                        gap="10px"
                        alignItems={'flex-start'}
                        width="100%"
                        sx={{
                          border: `1px solid ${GRAY_COLORS[300]}`,
                          borderRadius: '12px',
                          padding: '16px'
                        }}
                      >
                        <Box marginTop={1}>
                          <File04 />
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: 14, fontWeight: 600, textAlign: 'left' }} color="info">
                            {category?.name}
                          </Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 400, textAlign: 'left' }}>
                            {file?.name}
                          </Typography>
                          <Typography variant="small">{formatFileSize(file?.size)}</Typography>
                        </Box>
                        <IconButton
                          sx={{ marginLeft: 'auto', padding: 0, marginTop: 1 }}
                          onClick={() => handleDeleteFile(category.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )
                  })}
                </Box>
              )}
            </>
          )}
          {currentModal === 'CHOOSE_ALTERNATIVE' && (
            <Box display="flex" gap="20px" flexDirection="column" marginTop="20px">
              {alternatives?.alternatives?.map((item, index) => (
                <Box
                  key={item?.id}
                  onClick={() => setSelectedAlternative(item?.id)}
                  sx={{
                    border: `1px solid ${selectedAlternative === item?.id ? INFO_COLORS[500] : GRAY_COLORS[300]}`,
                    borderRadius: '12px',
                    padding: '16px',
                    cursor: 'pointer',
                    transition: 'border-color 0.3s'
                  }}
                >
                  <Box display="flex" justifyContent="space-between" flexDirection="row" alignItems="center">
                    <Typography fontSize={16} fontWeight={600}>
                      Предложение {index + 1}
                    </Typography>
                    <Checkbox
                      sx={{ margin: 0, padding: 0 }}
                      checked={selectedAlternative === item?.id}
                      onChange={() => setSelectedAlternative(item?.id)}
                    />
                  </Box>
                  <Divider
                    sx={{
                      marginLeft: '-16px',
                      marginRight: '-16px',
                      marginTop: '16px',
                      marginBottom: '16px',
                      borderColor: `${selectedAlternative === item?.id ? INFO_COLORS[500] : GRAY_COLORS[300]}`
                    }}
                  />
                  <Box display="grid" gridTemplateColumns={`repeat(3, 1fr)`} gap={4}>
                    <FieldInfoNG label={'Сумма займа'} isVertical>
                      <Typography
                        sx={{ fontSize: 14, fontWeight: 600, wordBreak: 'break-word', color: GRAY_COLORS[900] }}
                      >
                        {item?.loanAmount?.toString()?.thousandSeparator()} ₸
                      </Typography>
                    </FieldInfoNG>
                    <FieldInfoNG label={'Ставка'} isVertical>
                      <Typography
                        sx={{ fontSize: 14, fontWeight: 600, wordBreak: 'break-word', color: GRAY_COLORS[900] }}
                      >
                        {item?.interestRate} %
                      </Typography>
                    </FieldInfoNG>
                    <FieldInfoNG label={'Первоначальный взнос'} isVertical>
                      <Typography
                        sx={{ fontSize: 14, fontWeight: 600, wordBreak: 'break-word', color: GRAY_COLORS[900] }}
                      >
                        {item?.downPaymentPercentage} %
                      </Typography>
                    </FieldInfoNG>
                    <FieldInfoNG label={'Срок'} isVertical>
                      <Typography
                        sx={{ fontSize: 14, fontWeight: 600, wordBreak: 'break-word', color: GRAY_COLORS[900] }}
                      >
                        {item?.numberOfTranches} месяцев
                      </Typography>
                    </FieldInfoNG>
                    <FieldInfoNG label={'Гарант'} isVertical>
                      <Typography
                        sx={{ fontSize: 14, fontWeight: 600, wordBreak: 'break-word', color: GRAY_COLORS[900] }}
                      >
                        {item?.guarantee ? 'Требуется' : 'Не требуется'}
                      </Typography>
                    </FieldInfoNG>
                    <FieldInfoNG label={'Комментарий'} isVertical>
                      <Typography
                        sx={{ fontSize: 14, fontWeight: 600, wordBreak: 'break-word', color: GRAY_COLORS[900] }}
                      >
                        {item?.comment || 'Отсутствует'}
                      </Typography>
                    </FieldInfoNG>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          {currentModal === 'UPLOAD_ISSUED_DOCS' && (
            <>
              {!selectedIssuedGroup ? (
                <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2} mt={2}>
                  {Object.values(issuedGroups).map(g => (
                    <Box
                      key={g.key}
                      onClick={() => setSelectedIssuedGroup(g.key)}
                      sx={{
                        border: `1px solid ${GRAY_COLORS?.[300] || '#e5e7eb'}`,
                        borderRadius: '12px',
                        p: 2,
                        cursor: 'pointer',
                        transition: 'border-color .2s ease',
                        '&:hover': { borderColor: INFO_COLORS?.[500] || '#3b82f6' }
                      }}
                    >
                      <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>{g.title || g.key}</Typography>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="body2" sx={{ color: GRAY_COLORS?.[600] || '#475569' }}>
                        Доступных типов: {countsByGroup?.[g.key] || 0}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" gap="15px" mt={2}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                    {issuedGroups[selectedIssuedGroup]?.title || 'Категории'}
                  </Typography>

                  {issuedGroupCategories.map(category => {
                    const file = filesByCategory[category.id]
                    const st = statusByCategory[category.id]

                    return !file ? (
                      <Box
                        key={category?.id}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        gap={2}
                        sx={{
                          border: `1px solid ${GRAY_COLORS[300]}`,
                          borderRadius: '12px',
                          padding: '16px',
                          cursor: 'pointer',
                          position: 'relative'
                        }}
                        onClick={() => fileInputClick(category.id)}
                        onDragOver={e => {
                          e.preventDefault()
                          e.stopPropagation()
                        }}
                        onDrop={e => {
                          e.preventDefault()
                          e.stopPropagation()
                          handleFileUpload(e, category.id)
                        }}
                      >
                        <input
                          id={`fileInput-${category.id}`}
                          type="file"
                          ref={el => (fileInputRefs.current[category.id] = el)}
                          hidden
                          onChange={e => handleFileUpload(e, category.id)}
                        />

                        <Box display="flex" flexDirection="column" alignItems="center">
                          <Typography sx={{ fontSize: 14, fontWeight: 600, textAlign: 'center' }} color="info">
                            {category?.name}
                          </Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 400, textAlign: 'center' }}>
                            Нажмите, чтобы загрузить
                          </Typography>
                          <Typography variant="small">Или перетащите документ в эту область</Typography>
                        </Box>

                        {st === 'pending' && (
                          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                            <CircularProgress size={18} />
                          </Box>
                        )}
                        {st === 'success' && (
                          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                            <CheckCircleOutline color="success" fontSize="small" />
                          </Box>
                        )}
                        {st === 'error' && (
                          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                            <ErrorOutline color="error" fontSize="small" />
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Box
                        key={category?.id}
                        display="flex"
                        gap="10px"
                        alignItems="flex-start"
                        width="100%"
                        sx={{
                          border: `1px solid ${GRAY_COLORS[300]}`,
                          borderRadius: '12px',
                          padding: '16px',
                          position: 'relative'
                        }}
                      >
                        <Box marginTop={1}>
                          <File04 />
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: 14, fontWeight: 600, textAlign: 'left' }} color="info">
                            {category?.name}
                          </Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 400, textAlign: 'left' }}>
                            {file?.name}
                          </Typography>
                          <Typography variant="small">{formatFileSize(file?.size)}</Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1} ml="auto">
                          {st === 'pending' && <CircularProgress size={20} />}
                          {st === 'success' && <CheckCircleOutline color="success" fontSize="small" />}
                          {st === 'error' && <ErrorOutline color="error" fontSize="small" />}
                          <IconButton sx={{ p: 0, mt: 1 }} onClick={() => handleDeleteFile(category.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    )
                  })}
                </Box>
              )}
            </>
          )}
        </Box>
      </ActionModal>
      {currentModal === 'GARANTEE_FILE' && <GaranteeFileModal openModal={true} closeModal={closeModal} />}
    </>
  )
}

export default ManagerModal
